import { resolveBalanceShiftsHighPriority } from "./planMagicBalanceShifts";

export const INTELLIGENT_PLANNING_PARAMS_NOT_FOUND =
	"intelligent-planning.params_not_found";

export const PLAN_MAGIC_LOCAL_CONFIG_KEY = "plan_magic_configurations";

export function responseHasErrorKey(response, key) {
	if (!response || !key) return false;
	if (response.key === key || response.code === key) return true;
	if (!Array.isArray(response.errors)) return false;
	return response.errors.some(
		(error) => error?.key === key || error?.code === key
	);
}

export function createDefaultMagicPlan({
	start = "",
	end = "",
	groupId = "",
	timelimit = 300,
} = {}) {
	return {
		start,
		end,
		group_id: groupId,
		employees: [],
		min_consecutive_workdays: { active: false, value: 0 },
		max_working_hours_between_restdays: { active: false, value: 0 },
		extra_hours_allowed: false,
		over_assign_needs: false,
		balance_working_hours: false,
		balance_shifts_by_employee: false,
		balance_shifts_by_employee_high_priority: false,
		different_weekly_shifts: { active: false, start: "", end: "" },
		different_weekly_shifts_until_friday: { active: false, start: "", end: "" },
		fraction_shifts: false,
		fraction_shifts_minutes: 0,
		concatenate_shifts_without_rests_per_day: false,
		minimum_daily_hours: { active: false, value: 0 },
		maximum_daily_hours: { active: false, value: 0 },
		min_resthours_between_workdays: { active: false, value: 0 },
		min_resthours_between_shifts: { active: false, value: 0 },
		max_resthours_between_shifts: { active: false, value: 0 },
		min_weekly_restdays: { active: false, value: 0 },
		min_consecutive_restdays: { active: false, value: 0 },
		min_consecutive_rest_hours: { active: false, value: 0 },
		max_consecutive_restdays: { active: false, value: 0 },
		rest_day_types: { active: false, value: [] },
		next_week_rest_if_shifts_assigned: { active: false, value: [] },
		maximize_high_load_shift_priorities: { active: false, value: [] },
		minimum_free_weekends: { active: false, value: 0 },
		minimum_weekly_consecutive_rest_hours: { active: false, value: 0 },
		minimum_working_hours_per_block: { active: false, value: 0 },
		maximum_working_hours_per_block: { active: false, value: 0 },
		minimum_rest_hours_between_blocks: { active: false, value: 0 },
		maximum_rest_hours_between_blocks: { active: false, value: 0 },
		rest_window_between_blocks: { active: false, value: { start: "", end: "" } },
		number_of_blocks: { active: false, value: 0 },
		max_weekly_days_with_multiple_blocks: { active: false, value: 0 },
		maximize_skills: false,
		maximize_function_priorities: false,
		maximize_shift_priorities: false,
		minimize_shift_changes: false,
		different_shifts_across_break: false,
		ubiquitous_employees: false,
		timelimit,
		timelimit_execution: 0,
		iterative_calculation_in_days: { active: false, value: 6 },
		separated_restdays: false,
		forbid_unassigned_shifts: false,
		avoid_unassigned_shifts: false,
		single_ubication_per_day: false,
		different_consecutive_shifts: false,
		max_consecutive_same_shift: { active: false, value: 0 },
		single_function_per_block: false,
		single_ubication_per_block: false,
	};
}

export function normalizeRestWindowBetweenBlocks(config) {
	const defaultValue = { start: "", end: "" };
	if (!config || typeof config !== "object") {
		return { active: false, value: { ...defaultValue } };
	}
	const value = config.value;
	if (!value || typeof value !== "object" || Array.isArray(value)) {
		return { active: !!config.active, value: { ...defaultValue } };
	}
	return {
		active: !!config.active,
		value: {
			start: value.start || "",
			end: value.end || "",
		},
	};
}

function toDateKey(value) {
	if (value === null || value === undefined || value === "") return "";
	const match = String(value).match(/^(\d{4}-\d{2}-\d{2})/);
	return match ? match[1] : String(value);
}

/**
 * La copia del navegador solo cubre el hueco anterior al primer lanzamiento
 * guardado en el servidor. El formato es el de plan_magic_configurations:
 * las horas objetivo van en _targetHoursPersistence, no en employees.
 */
export function readLocalPlanningParams(storage, groupId) {
	if (
		!storage
		|| groupId === undefined
		|| groupId === null
		|| groupId === ""
	) {
		return null;
	}

	let raw;
	try {
		raw = storage.getItem(PLAN_MAGIC_LOCAL_CONFIG_KEY);
	} catch (error) {
		return null;
	}
	if (!raw) return null;

	let configurations;
	try {
		configurations = JSON.parse(raw);
	} catch (error) {
		return null;
	}
	if (!configurations || typeof configurations !== "object") return null;

	const groupConfig = configurations[String(groupId)];
	if (!groupConfig || typeof groupConfig !== "object" || Array.isArray(groupConfig)) {
		return null;
	}

	const {
		_targetHoursPersistence,
		employees: _legacyEmployees,
		...configRest
	} = groupConfig;
	const persistence =
		_targetHoursPersistence && typeof _targetHoursPersistence === "object"
			? _targetHoursPersistence
			: null;

	return {
		...configRest,
		start: persistence?.start || "",
		end: persistence?.end || "",
		employees: Array.isArray(persistence?.employees) ? persistence.employees : [],
	};
}

function resolveNamedItems(catalog, names) {
	if (!Array.isArray(names) || !Array.isArray(catalog)) return [];
	return names
		.map((name) => catalog.find((item) => item && item.name === name))
		.filter(Boolean);
}

/**
 * Aplica el body del último lanzamiento sobre los valores por defecto.
 * Las fechas del periodo abierto mandan: la plantilla no arrastra el rango anterior.
 * Las horas objetivo solo vuelven si ese lanzamiento es del mismo intervalo.
 */
export function mergeSavedPlanningParams({
	defaults,
	params,
	start,
	end,
	groupId,
	dayTypes = [],
	shifts = [],
	recommendedTimelimit = 300,
}) {
	const source =
		params && typeof params === "object" && !Array.isArray(params) ? params : {};
	const {
		start: savedStart,
		end: savedEnd,
		employees: savedEmployees,
		...configRest
	} = source;

	const definedConfig = {};
	Object.entries(configRest).forEach(([key, value]) => {
		if (value !== null && value !== undefined) definedConfig[key] = value;
	});

	const persistedTimelimit = Number(definedConfig.timelimit);
	const timelimit =
		persistedTimelimit === 7200
			? recommendedTimelimit
			: Number.isFinite(persistedTimelimit) && persistedTimelimit > 0
				? persistedTimelimit
				: recommendedTimelimit;

	const intervalMatches =
		toDateKey(savedStart) !== "" &&
		toDateKey(savedStart) === toDateKey(start) &&
		toDateKey(savedEnd) === toDateKey(end);
	const employees =
		intervalMatches && Array.isArray(savedEmployees) ? savedEmployees : [];

	const magic_plan = {
		...defaults,
		...definedConfig,
		timelimit,
		rest_window_between_blocks: normalizeRestWindowBetweenBlocks(
			definedConfig.rest_window_between_blocks ||
				defaults.rest_window_between_blocks
		),
		balance_shifts_by_employee_high_priority: resolveBalanceShiftsHighPriority({
			...defaults,
			...definedConfig,
		}),
		start: start || "",
		end: end || "",
		group_id: groupId,
		employees,
	};

	return {
		magic_plan,
		selected_day_types: resolveNamedItems(
			dayTypes,
			magic_plan.rest_day_types?.value
		),
		selected_shifts: resolveNamedItems(
			shifts,
			magic_plan.next_week_rest_if_shifts_assigned?.value
		),
		selected_high_load_shifts: resolveNamedItems(
			shifts,
			magic_plan.maximize_high_load_shift_priorities?.value
		),
		shift_weekly_limits: (Array.isArray(magic_plan.shifts) ? magic_plan.shifts : [])
			.map((entry) => {
				if (!entry) return null;
				const shift = shifts.find(
					(item) => item && String(item.id) === String(entry.id)
				);
				if (!shift) return null;
				return {
					...shift,
					max_weekly_shift_assignments: entry.max_weekly_shift_assignments,
				};
			})
			.filter(Boolean),
	};
}
